import React, { useState } from 'react';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { FileSpreadsheet, Upload, CheckCircle2, AlertCircle, Loader2, Info } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function UploadHistoricalWeekly() {
  const [file, setFile] = useState(null);
  const [sheetName, setSheetName] = useState('Agencies Visit Count 2024');
  const [headerRow, setHeaderRow] = useState(2);
  const [nameCol, setNameCol] = useState(1);
  const [altNameCol, setAltNameCol] = useState(2);
  const [dataStartCol, setDataStartCol] = useState(3);
  const [uploading, setUploading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleFileChange = (e) => {
    const f = e.target.files[0];
    if (f && (f.name.endsWith('.xlsx') || f.name.endsWith('.xls'))) {
      setFile(f);
      setError(null);
    } else {
      setError('Please select an Excel file (.xlsx or .xls)');
    }
  };

  const handleUpload = async () => {
    if (!file) {
      toast.error('Please select a file');
      return;
    }
    setUploading(true);
    setResult(null);
    setError(null);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const params = new URLSearchParams({
        sheet_name: sheetName,
        header_row: headerRow,
        name_col: nameCol,
        alt_name_col: altNameCol,
        data_start_col: dataStartCol,
      });
      const res = await axios.post(`${API}/upload-historical-weekly?${params.toString()}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 300000,
      });
      setResult(res.data);
      toast.success(`Imported ${res.data.summary_records_created.toLocaleString()} weekly totals`);
    } catch (e) {
      const msg = e.response?.data?.detail || e.message || 'Upload failed';
      setError(msg);
      toast.error(msg);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-6 max-w-4xl mx-auto animate-in fade-in duration-500">
      <div>
        <h1 className="font-heading text-3xl font-bold text-foreground flex items-center gap-2">
          <FileSpreadsheet className="text-primary" /> Upload Historical Weekly Totals
        </h1>
        <p className="text-muted-foreground mt-1">
          Import legacy YTD agency-level visit totals so trends and analytics reach back to your historical data.
        </p>
      </div>

      <Card className="p-5 bg-blue-50 border-blue-200">
        <div className="flex items-start gap-3">
          <Info size={18} className="text-blue-600 mt-0.5 shrink-0" />
          <div className="text-sm text-blue-900 space-y-1">
            <p className="font-semibold">Expected format</p>
            <ul className="list-disc list-inside space-y-1 text-blue-800">
              <li>Each row = one agency. Column A is the agency name (column B can hold an alternate name).</li>
              <li>Column headers like <code className="bg-white px-1 rounded">Week of 5/1/21</code>, <code className="bg-white px-1 rounded">Final 8/2/25</code>, <code className="bg-white px-1 rounded">Proj 1 8/9/25</code>, <code className="bg-white px-1 rounded">Proj 2 8/9/25</code>.</li>
              <li>Cell values = the agency's weekly visit total (numeric).</li>
              <li>New agencies are auto-created. After import, agencies with zero visits in the last 12 months are marked inactive (you can toggle them back on the agency page).</li>
              <li>Re-uploading the same file is safe — duplicate (agency × week × type) entries are skipped.</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <div>
            <Label>Excel file <span className="text-red-500">*</span></Label>
            <Input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleFileChange}
              data-testid="historical-file-input"
            />
            {file && (
              <p className="text-xs text-muted-foreground mt-1">
                Selected: {file.name} ({(file.size / 1024).toFixed(1)} KB)
              </p>
            )}
          </div>

          <details className="text-sm">
            <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
              Advanced — sheet & column positions
            </summary>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3 p-4 bg-muted/30 rounded-lg">
              <div>
                <Label className="text-xs">Sheet name</Label>
                <Input value={sheetName} onChange={(e) => setSheetName(e.target.value)} data-testid="sheet-name-input" />
              </div>
              <div>
                <Label className="text-xs">Header row (1-indexed)</Label>
                <Input type="number" min="1" value={headerRow} onChange={(e) => setHeaderRow(parseInt(e.target.value) || 2)} data-testid="header-row-input" />
              </div>
              <div>
                <Label className="text-xs">Agency name column</Label>
                <Input type="number" min="1" value={nameCol} onChange={(e) => setNameCol(parseInt(e.target.value) || 1)} data-testid="name-col-input" />
              </div>
              <div>
                <Label className="text-xs">Alt name column</Label>
                <Input type="number" min="1" value={altNameCol} onChange={(e) => setAltNameCol(parseInt(e.target.value) || 2)} data-testid="alt-name-col-input" />
              </div>
              <div>
                <Label className="text-xs">Data start column</Label>
                <Input type="number" min="1" value={dataStartCol} onChange={(e) => setDataStartCol(parseInt(e.target.value) || 3)} data-testid="data-start-col-input" />
              </div>
            </div>
          </details>

          <Button
            onClick={handleUpload}
            disabled={!file || uploading}
            className="w-full"
            data-testid="upload-historical-btn"
          >
            {uploading ? (
              <>
                <Loader2 size={16} className="mr-2 animate-spin" />
                Processing... (may take a moment for large files)
              </>
            ) : (
              <>
                <Upload size={16} className="mr-2" />
                Upload & Import
              </>
            )}
          </Button>
        </div>
      </Card>

      {error && (
        <Card className="p-5 border-red-200 bg-red-50">
          <div className="flex items-start gap-3">
            <AlertCircle size={18} className="text-red-600 mt-0.5 shrink-0" />
            <div>
              <p className="font-semibold text-red-900">Upload failed</p>
              <p className="text-sm text-red-800 mt-1">{error}</p>
            </div>
          </div>
        </Card>
      )}

      {result && (
        <Card className="p-6 border-green-200 bg-green-50" data-testid="historical-upload-result">
          <div className="flex items-start gap-3">
            <CheckCircle2 size={20} className="text-green-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <p className="font-semibold text-green-900">Import complete</p>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-4">
                <div>
                  <p className="text-2xl font-bold text-green-900">{result.summary_records_created.toLocaleString()}</p>
                  <p className="text-xs text-green-800">Weekly totals imported</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-900">{result.agencies_created}</p>
                  <p className="text-xs text-green-800">New agencies created</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-amber-700">{result.agencies_marked_inactive}</p>
                  <p className="text-xs text-amber-700">Marked inactive (12mo idle)</p>
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-900">{result.agencies_marked_active}</p>
                  <p className="text-xs text-green-800">Re-marked active</p>
                </div>
              </div>
              {result.skipped_columns?.length > 0 && (
                <div className="mt-4 text-xs">
                  <p className="font-semibold text-amber-800">Skipped {result.skipped_columns.length} unrecognized columns:</p>
                  <p className="text-amber-700 mt-1">{result.skipped_columns.slice(0, 10).join(' · ')}</p>
                </div>
              )}
            </div>
          </div>
        </Card>
      )}
    </div>
  );
}
